import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { KestrelLoadingWidget } from 'features/kestrelViewer'
import { PixelStreamUI } from 'features/kestrelViewer/PixelStreamUI'
import React, { FC, useEffect, useState } from 'react'

import { useKestrel } from './KestrelProvider'

export const KestrelUI: FC<{ kestrelModelId: string }> = ({
  kestrelModelId,
}) => {
  /*
        This Class handles the negotiation to the Kestrel Matchmaker service to retrieve a
        valid Pixel Streaming URL

        If the matchmaker service returns a valid URL to stream the Kestrel scene, it will
        initialize the PixelStreamUI component. If not, it will display a message to the user.
    */

  const { authorization } = useGeckoAuth()
  const { KESTREL_MATCHMAKER_URL } = useRuntimeVariables()
  const { pixelStreamingURL, setPixelStreamingURL, kestrelStatus } =
    useKestrel()
  const reserveURL = `${KESTREL_MATCHMAKER_URL}/api/v1/kestrel/reserve`
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasTimedOut, setHasTimedOut] = useState(false)

  useEffect(() => {
    // Don't call /reserve until we have needed params
    if (!authorization || !reserveURL || !kestrelModelId) {
      return
    }

    const fetchStreamingURL = async () => {
      setIsLoading(true)
      setError(null)
      setHasTimedOut(false)

      try {
        const response = await fetch(reserveURL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${authorization}`,
          },
          body: JSON.stringify({
            kestrelModelId: kestrelModelId,
          }),
        })

        if (!response.ok) {
          throw new Error(
            `Failed to connect to matchmaker service: ${response.statusText}`
          )
        }

        const data = await response.text()
        const cleanUrl = `wss://${data.trim().replace(/"/g, '')}`
        setPixelStreamingURL(cleanUrl)
      } catch (error) {
        console.error('Error connecting to matchmaker:', error)
        setError(
          error instanceof Error ? error.message : 'An unknown error occurred'
        )
      } finally {
        setIsLoading(false)
      }
    }

    fetchStreamingURL()
  }, [
    authorization,
    kestrelModelId,
    reserveURL,
    setPixelStreamingURL,
    setIsLoading,
    setError,
  ])

  // Set up 2-minute timeout when we get pixelStreamingURL
  useEffect(() => {
    if (!pixelStreamingURL) return

    const timeout = setTimeout(
      () => {
        setHasTimedOut(true)
      },
      2 * 60 * 1000
    ) // 2 minutes

    return () => clearTimeout(timeout)
  }, [pixelStreamingURL])

  // Show loading until both VM is connected and websocket stream is ready, or until timeout
  const showLoading =
    isLoading ||
    (pixelStreamingURL && !kestrelStatus?.videoReady && !hasTimedOut)

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
      }}>
      {showLoading && <KestrelLoadingWidget />}
      {!isLoading && pixelStreamingURL && <PixelStreamUI />}
      {!isLoading && !pixelStreamingURL && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            color: '#ff4444',
            textAlign: 'center',
            padding: '20px',
          }}>
          <p>{error || 'No pixel streaming URL found'}</p>
        </div>
      )}
    </div>
  )
}
